import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { BellIcon } from '@heroicons/react/24/outline';
import { formatNotificationCount } from 'utils/notifications';

const NotificationButton = forwardRef(function NotificationButton({
  count = 0,
  showDot = false,
  isOpen = false,
  hasPopup = 'dialog',
  className,
  disabled = false,
  onClick,
  ...props
}, ref) {
  const normalizedCount = Math.max(0, Number(count) || 0);
  const hasCount = normalizedCount > 0;
  const shouldShowDot = showDot && !hasCount;
  const countLabel = normalizedCount > 99 ? '99 or more' : String(normalizedCount);
  const ariaLabel = hasCount
    ? `View notifications, ${countLabel} unread`
    : 'View notifications';

  return (
    <button
      ref={ref}
      type="button"
      aria-label={ariaLabel}
      aria-haspopup={hasPopup || undefined}
      aria-expanded={hasPopup ? isOpen : undefined}
      disabled={disabled}
      onClick={onClick}
      className={classNames(
        'relative rounded-full p-1 text-gray-400 transition hover:text-white focus:outline-2 focus:outline-offset-2 focus:outline-indigo-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60',
        isOpen && 'text-white',
        className
      )}
      {...props}
    >
      <span className="absolute -inset-1.5" />
      <BellIcon className="size-6" aria-hidden="true" />
      {hasCount && (
        <span
          aria-hidden="true"
          className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-gray-800"
        >
          {formatNotificationCount(normalizedCount)}
        </span>
      )}
      {shouldShowDot && (
        <span
          aria-hidden="true"
          className="absolute right-0 top-0 size-2.5 rounded-full bg-indigo-400 ring-2 ring-gray-800"
        />
      )}
    </button>
  );
});

export default NotificationButton;
